import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { Auth, Role, ROLES } from './types';
import { RootState } from '../store';
import { CALIBRATION_LAB_COMPANY_NAME } from '../../constants/companies';
import { clearAllThreads } from '../../pages/artycAI/chatStorage';

export interface AuthState {
  auth: Auth | null;
}

const initialState: AuthState = {
  auth: null,
};

// Persist the active-org selection so it survives token refreshes.
const ACTIVE_ORG_KEY = 'artyc.activeCompanyId';
const readPersistedActiveOrg = (): string | undefined => {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY) || undefined;
  } catch {
    return undefined;
  }
};
const persistActiveOrg = (id: string | undefined) => {
  try {
    if (id) localStorage.setItem(ACTIVE_ORG_KEY, id);
    else localStorage.removeItem(ACTIVE_ORG_KEY);
  } catch {
    /* ignore */
  }
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuth: (state, { payload }: PayloadAction<Auth>) => {
      const homeCompanyId = payload.companyId;
      // Re-apply a persisted active-org selection across token refreshes.
      const persisted = readPersistedActiveOrg();
      state.auth = {
        ...payload,
        homeCompanyId,
        activeCompanyId: persisted,
        // `companyId` is the effective/active org used to build API URLs.
        companyId: persisted ?? homeCompanyId,
      };
    },
    clearAuth: (state) => {
      state.auth = null;
      persistActiveOrg(undefined);
      clearAllThreads();
    },
    // Switch the active org. Selecting the home company clears the override.
    setActiveCompany: (state, { payload }: PayloadAction<string>) => {
      if (!state.auth) return;
      const home = state.auth.homeCompanyId ?? state.auth.companyId;
      const isHome = payload === home;
      state.auth.activeCompanyId = isHome ? undefined : payload;
      state.auth.companyId = payload;
      persistActiveOrg(isHome ? undefined : payload);
    },
  },
});

export const { setAuth, clearAuth, setActiveCompany } = authSlice.actions;

export const selectAuth = (state: RootState) => state.auth.auth;

export default authSlice.reducer;

export const isArtycAdmin = (auth: Auth | null): boolean =>
  auth?.role === ROLES.ARTYC_ADMIN.pid;
export const isArtycEmployee = (auth: Auth | null): boolean =>
  auth?.role === ROLES.ARTYC_EMPLOYEE.pid;
export const isArtyc = (auth: Auth | null): boolean =>
  isArtycAdmin(auth) || isArtycEmployee(auth);
export const isViewer = (auth: Auth | null): boolean =>
  auth?.role === ROLES.VIEWER.pid;

// Artyc staff and Calibration Lab users may run device calibrations.
export const selectCanRunCalibrations = (state: RootState): boolean => {
  const auth = state.auth.auth;
  if (isArtyc(auth)) return true;
  const userCompany = state.companies.data.find(
    (company) => company._id === auth?.companyId
  );
  return (
    (userCompany?.companyName ?? '').trim().toLowerCase() ===
    CALIBRATION_LAB_COMPANY_NAME
  );
};

export const isCompanyEmployee = (auth: Auth | null): boolean =>
  auth?.role === ROLES.COMPANY_EMPLOYEE.pid;
export const isCompanyAdmin = (auth: Auth | null): boolean =>
  auth?.role === ROLES.COMPANY_ADMIN.pid;
export const isCompanyUser = (auth: Auth | null) =>
  isCompanyAdmin(auth) || isCompanyEmployee(auth);

// Org Admin is the existing Company Admin role.
export const isOrgAdmin = isCompanyAdmin;

// Only Artyc Admins and Org Admins can switch the active org.
export const canSwitchOrg = (auth: Auth | null): boolean =>
  isArtycAdmin(auth) || isOrgAdmin(auth);

export const isScopedUser = (auth: Auth | null): boolean =>
  auth?.role === ROLES.ADMIN.pid ||
  auth?.role === ROLES.OPERATOR.pid ||
  auth?.role === ROLES.VIEWER.pid;

// The scoped group-level "Admin" role, distinct from the Org/Company Admin.
export const isScopedAdmin = (auth: Auth | null): boolean =>
  auth?.role === ROLES.ADMIN.pid;

// Roles that may access the main portal (journeys, events, shippers, etc.).
export const PORTAL_ROLES: Role[] = [
  ROLES.ARTYC_ADMIN,
  ROLES.ARTYC_EMPLOYEE,
  ROLES.COMPANY_ADMIN,
  ROLES.COMPANY_EMPLOYEE,
  ROLES.ADMIN,
  ROLES.OPERATOR,
  ROLES.VIEWER,
];
